import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from 'components/Icons'
import { BottomNav } from 'components'
import GlobalSearch from 'components/UnvUI/GlobalSearch'
import { getMe } from 'service/AdminPanelService'
import { setUser, clearUser, setLoading } from 'store/slices/AuthSlice'
import { filterSidebarByRoles } from 'constants/sidebarLinks'
import chuLogo from 'assets/img/chu.svg'
import LangSwitcher from 'components/LangSwitcher/LangSwitcher'
import { LayoutContext } from '../LayoutContext'
import styles from './UnvLayout.module.scss'

// Маппинг роутов на иконки из icons-registry
const ROUTE_ICON_MAP = {
  '/home/': 'home',
  '/schedule/': 'schedule',
  '/schedule-builder/': 'calendar-plus',
  '/schedule-import/': 'cloud-upload',
  '/sessions/': 'exam',
  '/my-syllabus/': 'book-open',
  '/syllabuses/': 'syllabus',
  '/syllabus-builder/': 'edit-2',
  '/syllabus-diff/': 'list',
  '/syllabus-approval/': 'check-square',
  '/journal/': 'journal',
  '/gradebook/': 'gradebook',
  '/assignments-list/': 'assignment',
  '/tests/': 'test',
  '/test-builder/': 'edit',
  '/question-bank/': 'database',
  '/take-test/': 'test',
  '/statements/': 'file-text',
  '/statements-list/': 'file-text',
  '/teacher-statements/': 'file-text',
  '/orders/': 'document',
  '/tasks/': 'check-square',
  '/calendar/': 'calendar',
  '/notes/': 'edit-2',
  '/chats/': 'message-circle',
  '/admin/': 'cog',
  '/admin/integrations/': 'package',
  '/admin/services/': 'package',
  '/students-list/': 'users',
  '/employees/': 'briefcase',
  '/admission/': 'user-plus',
  '/transfers/': 'arrow-right',
  '/employee-wizard/': 'user-plus',
  '/groups/': 'group',
  '/reports/': 'bar',
  '/reports/form-1nk/': 'file-text',
  '/reports/contingent/': 'list',
  '/reports/accreditation/': 'award',
  '/dean-dashboard/': 'pie',
  '/curator-drilldown/': 'activity',
  '/student-home/': 'student',
  '/teacher-home/': 'teacher',
  '/library/': 'library',
  '/dorm/': 'dorm',
  '/practice/': 'briefcase',
  '/diploma/': 'graduation',
  '/accounting/': 'wallet',
  '/accounting-info/': 'pie',
  '/archive/': 'archive',
  '/archive/ocr-search/': 'search',
  '/archive/order-link/': 'link',
  '/distance/': 'video',
  '/distance/lectures/': 'play',
  '/distance/virtual/': 'camera',
  '/postgrad/': 'graduation',
  '/foreign-students/': 'globe',
  '/military-students/': 'shield',
  '/dpo/': 'award',
  '/student-life/': 'heart',
  '/science/': 'flame',
  '/career/': 'target',
  '/plagiarism/': 'check-double',
  '/help/': 'help-circle',
  '/help/api/': 'server',
  '/personalcard/': 'user',
  '/structure/': 'layers',
  '/statistic/': 'bar',
  '/all-statistic/': 'pie',
  '/surveys/': 'list',
  '/report-card/': 'gradebook',
  '/workload/': 'briefcase',
  '/my-disciplines/': 'book-open',
  '/2fa-setup/': 'shield-check',
  '/account-locked/': 'lock',
}

function getIconForRoute(to) {
  if (ROUTE_ICON_MAP[to]) return ROUTE_ICON_MAP[to]
  // Fallback: try prefix match
  for (const k of Object.keys(ROUTE_ICON_MAP)) {
    if (to.startsWith(k)) return ROUTE_ICON_MAP[k]
  }
  return 'list'
}

// Mobile nav items use translation keys (resolved through useTranslation).
// `label` holds the i18n key to lookup in the `menu` namespace, `fallback` is the RU original.
const MOBILE_NAV_ITEMS_BY_ROLE = {
  student: [
    { key: 'home', icon: 'home', label: 'menu.home', fallback: 'Главная', path: '/home/' },
    { key: 'schedule', icon: 'schedule', label: 'menu.schedule', fallback: 'Расписание', path: '/schedule/' },
    { key: 'gradebook', icon: 'gradebook', label: 'menu.gradebook', fallback: 'Оценки', path: '/gradebook/' },
    { key: 'tasks', icon: 'check-square', label: 'menu.assignments', fallback: 'Задания', path: '/assignments-list/' },
    { key: 'profile', icon: 'user', label: 'menu.profile', fallback: 'Профиль', path: '/personalcard/' },
  ],
  teacher: [
    { key: 'home', icon: 'home', label: 'menu.home', fallback: 'Главная', path: '/home/' },
    { key: 'schedule', icon: 'schedule', label: 'menu.schedule', fallback: 'Расписание', path: '/schedule/' },
    { key: 'journal', icon: 'journal', label: 'menu.journal', fallback: 'Журнал', path: '/journal/' },
    { key: 'assignments', icon: 'assignment', label: 'menu.assignments', fallback: 'Задания', path: '/assignments-list/' },
    { key: 'profile', icon: 'user', label: 'menu.profile', fallback: 'Профиль', path: '/personalcard/' },
  ],
  hr_students: [
    { key: 'home', icon: 'home', label: 'menu.home', fallback: 'Главная', path: '/home/' },
    { key: 'students', icon: 'users', label: 'menu.studentsList', fallback: 'Студенты', path: '/students-list/' },
    { key: 'admission', icon: 'user-plus', label: 'menu.admission', fallback: 'Приём', path: '/admission/' },
    { key: 'orders', icon: 'document', label: 'menu.orders', fallback: 'Приказы', path: '/orders/' },
    { key: 'profile', icon: 'user', label: 'menu.profile', fallback: 'Профиль', path: '/personalcard/' },
  ],
  hr_employees: [
    { key: 'home', icon: 'home', label: 'menu.home', fallback: 'Главная', path: '/home/' },
    { key: 'employees', icon: 'briefcase', label: 'menu.employees', fallback: 'Сотрудники', path: '/employees/' },
    { key: 'orders', icon: 'document', label: 'menu.orders', fallback: 'Приказы', path: '/orders/' },
    { key: 'profile', icon: 'user', label: 'menu.profile', fallback: 'Профиль', path: '/personalcard/' },
  ],
  methodist: [
    { key: 'home', icon: 'home', label: 'menu.home', fallback: 'Главная', path: '/home/' },
    { key: 'schedule', icon: 'schedule', label: 'menu.schedule', fallback: 'Расписание', path: '/schedule/' },
    { key: 'syllabus', icon: 'syllabus', label: 'menu.syllabus', fallback: 'Учебные планы', path: '/syllabuses/' },
    { key: 'reports', icon: 'bar', label: 'menu.reports', fallback: 'Отчёты', path: '/reports/' },
    { key: 'profile', icon: 'user', label: 'menu.profile', fallback: 'Профиль', path: '/personalcard/' },
  ],
  accountant: [
    { key: 'home', icon: 'home', label: 'menu.home', fallback: 'Главная', path: '/home/' },
    { key: 'accounting', icon: 'wallet', label: 'menu.accounting', fallback: 'Бухгалтерия', path: '/accounting/' },
    { key: 'dorm', icon: 'dorm', label: 'menu.dorm', fallback: 'Общежитие', path: '/dorm/' },
    { key: 'profile', icon: 'user', label: 'menu.profile', fallback: 'Профиль', path: '/personalcard/' },
  ],
  archivist: [
    { key: 'home', icon: 'home', label: 'menu.home', fallback: 'Главная', path: '/home/' },
    { key: 'archive', icon: 'archive', label: 'menu.archive', fallback: 'Архив', path: '/archive/' },
    { key: 'profile', icon: 'user', label: 'menu.profile', fallback: 'Профиль', path: '/personalcard/' },
  ],
  parent: [
    { key: 'home', icon: 'home', label: 'menu.home', fallback: 'Главная', path: '/home/' },
    { key: 'library', icon: 'library', label: 'menu.library', fallback: 'Библиотека', path: '/library/' },
    { key: 'profile', icon: 'user', label: 'menu.profile', fallback: 'Профиль', path: '/personalcard/' },
  ],
  // Для admin/sysadmin/rectorate показываем расширенное меню.
  admin: [
    { key: 'home', icon: 'home', label: 'menu.home', fallback: 'Главная', path: '/home/' },
    { key: 'schedule', icon: 'schedule', label: 'menu.schedule', fallback: 'Расписание', path: '/schedule/' },
    { key: 'admin', icon: 'cog', label: 'menu.admin', fallback: 'Админ', path: '/admin/' },
    { key: 'chat', icon: 'message-circle', label: 'menu.chat', fallback: 'Чат', path: '/chats/' },
    { key: 'profile', icon: 'user', label: 'menu.profile', fallback: 'Профиль', path: '/personalcard/' },
  ],
  // Безопасный default — без /admin/ (его не каждый видит).
  default: [
    { key: 'home', icon: 'home', label: 'menu.home', fallback: 'Главная', path: '/home/' },
    { key: 'schedule', icon: 'schedule', label: 'menu.schedule', fallback: 'Расписание', path: '/schedule/' },
    { key: 'chat', icon: 'message-circle', label: 'menu.chat', fallback: 'Чат', path: '/chats/' },
    { key: 'profile', icon: 'user', label: 'menu.profile', fallback: 'Профиль', path: '/personalcard/' },
  ],
}

function UnvLayout({ children, title }) {
  const { t } = useTranslation('common')
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const auth = useSelector((state) => state.auth || {})
  const me = auth.me
  const roles = auth.roles || []
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light'
    try {
      return localStorage.getItem('theme') === 'dark' ? 'dark' : 'light'
    } catch (_) {
      return 'light'
    }
  })

  useEffect(() => {
    const root = document.documentElement
    if (theme === 'dark') root.setAttribute('data-theme', 'dark')
    else root.removeAttribute('data-theme')
    try {
      localStorage.setItem('theme', theme)
    } catch (_) {
      /* ignore */
    }
  }, [theme])

  const toggleTheme = () => setTheme((th) => (th === 'dark' ? 'light' : 'dark'))

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) return
    if (auth.loaded || auth.loading) return
    dispatch(setLoading(true))
    getMe()
      .then((res) => {
        dispatch(setUser(res?.data || res))
      })
      .catch(() => {
        dispatch(setLoading(false))
      })
  }, [auth.loaded, auth.loading, dispatch])

  const sidebarItems = useMemo(() => {
    const codes = (roles || []).map((r) => (typeof r === 'string' ? r : r?.code))
    return filterSidebarByRoles(codes, { isSuper: !!me?.is_superuser })
  }, [roles, me?.is_superuser])

  const primaryRoleLabel = useMemo(() => {
    if (!auth.primaryRole) {
      if (!roles || !roles.length) return ''
      const first = roles[0]
      return typeof first === 'string' ? first : first?.title || first?.code || ''
    }
    if (typeof auth.primaryRole === 'string') return auth.primaryRole
    return auth.primaryRole?.title || auth.primaryRole?.code || ''
  }, [auth.primaryRole, roles])

  const handleLogout = () => {
    try {
      localStorage.removeItem('token')
      localStorage.removeItem('refresh')
      localStorage.removeItem('login_roles')
      localStorage.removeItem('login_primary_role')
      localStorage.removeItem('user')
    } catch (_) {
      /* ignore */
    }
    dispatch(clearUser())
    navigate('/login', { replace: true })
  }

  // Уведомления (skeleton).
  const [notifOpen, setNotifOpen] = useState(false)
  const notifRef = useRef(null)
  useEffect(() => {
    if (!notifOpen) return undefined
    const onClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false)
      }
    }
    const onKey = (e) => { if (e.key === 'Escape') setNotifOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [notifOpen])

  const fullName =
    me?.full_name ||
    [me?.last_name, me?.first_name].filter(Boolean).join(' ') ||
    me?.username ||
    ''

  const initials = fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase() || '?'

  const roleCode = useMemo(() => {
    const codes = (roles || []).map((r) => (typeof r === 'string' ? r : r?.code))
    // Приоритет: специальные роли → общие.
    if (codes.includes('student')) return 'student'
    if (codes.includes('teacher')) return 'teacher'
    if (codes.includes('hr_students')) return 'hr_students'
    if (codes.includes('hr_employees')) return 'hr_employees'
    if (codes.includes('methodist')) return 'methodist'
    if (codes.includes('accountant')) return 'accountant'
    if (codes.includes('archivist')) return 'archivist'
    if (codes.includes('parent')) return 'parent'
    if (codes.includes('rectorate') || codes.includes('sysadmin')) return 'admin'
    return 'default'
  }, [roles])

  const mobileNavItems = (MOBILE_NAV_ITEMS_BY_ROLE[roleCode] || MOBILE_NAV_ITEMS_BY_ROLE.default).map((item) => ({
    ...item,
    label: t(item.label, item.fallback),
  }))

  const closeMobileMenu = () => setMobileOpen(false)

  return (
    <LayoutContext.Provider value={{ inLayout: true }}>
    <div className={styles.shell}>
      {mobileOpen && (
        <button
          type="button"
          className={styles.mobileOverlay}
          onClick={closeMobileMenu}
          onKeyDown={(e) => {
            if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              closeMobileMenu()
            }
          }}
          aria-label={t('auto.unvlayout.close_menu', 'Закрыть меню')}
        />
      )}

      <aside className={`${styles.sidebar} ${collapsed ? styles.sidebar_collapsed : ''} ${mobileOpen ? styles.sidebar_mobileOpen : ''}`}>
        <div className={styles.brand}>
          <span className={styles.brand_logo}>
            <img src={chuLogo} alt="ЧУ" className={styles.brand_logo_img} />
          </span>
          {!collapsed && <span className={styles.brand_name}>{t('common.chu_full_name', 'Чуйский Университет')}</span>}
          <button
            type="button"
            className={styles.collapseBtn}
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? t('auto.unvlayout.expand_menu', 'Развернуть меню') : t('auto.unvlayout.collapse_menu', 'Свернуть меню')}
            title={collapsed ? t('auto.unvlayout.expand', 'Развернуть') : t('auto.unvlayout.collapse', 'Свернуть')}
          >
            <Icon name={collapsed ? 'chevron-right' : 'chevron-left'} size={14} />
          </button>
        </div>

        {fullName && !collapsed ? (
          <div className={styles.user}>
            <div className={styles.userAvatar} aria-hidden="true">{initials}</div>
            <div className={styles.userInfo}>
              <div className={styles.user_name}>{fullName}</div>
              {primaryRoleLabel ? (
                <div className={styles.user_role}>{primaryRoleLabel}</div>
              ) : null}
            </div>
          </div>
        ) : null}

        <nav className={styles.menu} aria-label={t('auto.unvlayout.main_menu', 'Главное меню')}>
          {sidebarItems.map((item) => {
            const iconName = getIconForRoute(item.to)
            const labelKey = item.key
            const labelText = labelKey ? t(labelKey, item.text) : item.text
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `${styles.link} ${isActive ? styles.link_active : ''}`
                }
                end
                title={collapsed ? labelText : undefined}
              >
                <span className={styles.linkIcon}>
                  <Icon name={iconName} size={18} color="currentColor" />
                </span>
                {!collapsed && <span className={styles.linkText}>{labelText}</span>}
              </NavLink>
            )
          })}
        </nav>

        <button
          type="button"
          className={styles.footer_btn}
          onClick={handleLogout}
          title={collapsed ? t('components.unvLayout.logout', 'Выйти') : undefined}
        >
          <Icon name="logout" size={18} color="currentColor" />
          {!collapsed && <span>{t('components.unvLayout.logout', 'Выйти')}</span>}
        </button>
      </aside>

      <div className={styles.main}>
        <div className={styles.topbar}>
          <button
            type="button"
            className={styles.mobileToggle}
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={t('auto.unvlayout.open_menu', 'Открыть меню')}
          >
            <Icon name="menu" size={20} />
          </button>
          <div className={styles.topbar_title}>{title || ''}</div>
          <GlobalSearch />
          <div className={styles.topbar_right}>
            <LangSwitcher />
            <button
              type="button"
              className={styles.topbarIconBtn}
              onClick={toggleTheme}
              title={theme === 'dark' ? t('auto.unvlayout.light_mode', 'Светлая тема') : t('auto.unvlayout.dark_mode', 'Тёмная тема')}
              aria-label={theme === 'dark' ? t('auto.unvlayout.light_mode', 'Светлая тема') : t('auto.unvlayout.dark_mode', 'Тёмная тема')}
            >
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={18} />
            </button>
            <div style={{ position: 'relative' }} ref={notifRef}>
              <button
                type="button"
                className={styles.topbarIconBtn}
                onClick={() => setNotifOpen((o) => !o)}
                title={t('auto.unvlayout.notifications', 'Уведомления')}
                aria-label={t('auto.unvlayout.notifications', 'Уведомления')}
                aria-expanded={notifOpen}
                aria-haspopup="true"
              >
                <Icon name="bell" size={18} />
              </button>
              {notifOpen ? (
                <div
                  role="menu"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    minWidth: 280,
                    maxWidth: 360,
                    background: 'var(--color-surface, #fff)',
                    color: 'var(--color-text, inherit)',
                    border: '1px solid var(--color-border, #e5e7eb)',
                    borderRadius: 8,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                    padding: 12,
                    zIndex: 1000,
                  }}
                >
                  <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>
                    {t('notification.alerts', 'Уведомления')}
                  </h4>
                  <div style={{ fontSize: 13, color: 'var(--color-muted, #6b7280)' }}>
                    {t('notification.empty', 'Уведомлений нет')}
                  </div>
                </div>
              ) : null}
            </div>
            <button type="button" className={styles.topbarIconBtn} onClick={() => navigate('/personalcard/')} title={t('auto.unvlayout.profile', 'Профиль')} aria-label={t('auto.unvlayout.profile', 'Профиль')}>
              <Icon name="user" size={18} />
            </button>
          </div>
        </div>
        <div className={styles.content}>{children}</div>
      </div>

      <BottomNav items={mobileNavItems} hidden={mobileOpen} />
    </div>
    </LayoutContext.Provider>
  )
}

export default UnvLayout
